import { getBinLevelMeasurementOverrides, Measurements } from '~/utility/BinSentry-ui-utility';
import { DateTime } from 'luxon';
import { css, html, LitEntity, renderIf, SirenActionMixin } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import './bin-levels';
import _ from 'lodash';

export const getRefillRange = function({ totalVolume, bulkDensity, currentLevelIndex, levels }) {
  const currentLevel = levels[currentLevelIndex];
  if (currentLevel?.properties.label !== 'REFILL') {
    return;
  }

  const priorUnlabeledLevel = _.findLast(levels, level => {
    return !level.properties.label;
  }, currentLevelIndex);
  if (!priorUnlabeledLevel) {
    return;
  }

  const margin = Math.max(totalVolume * .05, 1000 / bulkDensity);
  const vDiff = currentLevel.properties.volume - priorUnlabeledLevel.properties.volume;
  return {
    low: vDiff - margin,
    high: vDiff + margin,
  };
};

class BinReadings extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.readings = [];
  }

  static get properties() {
    return {
      start: { type: String },
      end: { type: String },
      readings: { type: Array, attribute: false },
      prevLink: { type: Object, attribute: false },
      nextLink: { type: Object, attribute: false },
      levelsHref: { type: String, attribute: false },
      disableNext: { type: Boolean, attribute: false },
      binLevelMeasurement: { type: Object, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      .number {
        text-align: right;
      }
      .list-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 1rem 0;
        padding: 0;
      }
      .list-actions button {
        margin: 0;
      }
    `];
  }

  render() {
    const showPercent = this.binLevelMeasurement?.unit === '%';
    return html`
      ${renderIf(this.binLevelMeasurement, () => html`
        ${this._renderListActions()}
        <table class="readings">
        <thead>
          <tr>
            <th>Date</th>
            <th class="number">Volume</th>
            <th class="number">${showPercent ? 'Percent Full' : 'Weight'}</th>
            <th>Additional Information</th>
          </tr>
        </thead>
        <tbody>
          ${this.readings.map(reading => this._renderReading(reading, showPercent))}
        </tbody>
        </table>
        ${this._renderListActions()}
        <bin-levels
          .href="${this.levelsHref}"
          .start="${this.start}"
          .end="${this.end}"
          @update="${this._onUpdateLevels}"
        ></bin-levels>
      `)}
    `;
  }

  _renderListActions() {
    return html`
      <div class="list-actions">
        ${renderIf(this.prevLink, html`
          <button class="button button--tertiary" @click="${this._prev}">Prev</button>
        `)}

        ${renderIf(this.prevLink || this.nextLink, html`
          <span>
            ${renderIf(this.prevLink, html`<span class="date">${this._formatDate(this.start)}</span>`)}
            ${renderIf(this.prevLink && this.nextLink, ' - ')}
            ${renderIf(this.nextLink, html`<span class="date">${this._formatDate(this.end)}</span>`)}
          </span>
        `)}

        ${renderIf(this.nextLink, html`
          <button class="button button--tertiary" @click="${this._next}" ?disabled="${this.disableNext}">Next</button>
        `)}
      </div>
    `;
  }

  _renderReading(reading, showPercent) {
    const { publishedAt, label, volume } = reading.level.properties;
    const extra = [];
    if (label === 'REFILL') {
      const amount = reading.refillAmount ? reading.refillAmount : '';
      extra.push(`REFILL ${amount}`);
    }
    return html`
      <tr class="reading">
        <td>${this._formatDate(publishedAt)}</td>
        <td class="number">${this._formatVolume(volume)}</td>
        <td class="number">${showPercent ? this._formatPercent(volume) : this._formatWeight(volume)}</td>
        <td>${reading.info || ''}</td>
      </tr>
    `;
  }

  async _entityChanged(entity) {
    super._entityChanged(entity);
    try {
      const levelsEntityIndex = await this.fetchSiren(this.getHref('https://api.binsentry.com/rel/v2/bin-levels'));
      if (!levelsEntityIndex) {
        return;
      }
      const dateRangeAction = levelsEntityIndex.getActionByName('custom-date-range');
      const fields = this.getActionFields(dateRangeAction);
      const now = DateTime.utc();
      fields.set('startDate', now.minus({ week: 1 }).toISO());
      fields.set('endDate', now.toISO());
      this.levelsHref = this.parseAction(dateRangeAction, fields).url.href;
      this.binLevelMeasurement = Measurements.get('binLevel', await getBinLevelMeasurementOverrides(this.entity));
    } catch (e) {
      if (e.error?.code === 404) {
        return;
      }
      throw e;
    }
  }

  _prev() {
    if (!this.prevLink) {
      return;
    }
    this.levelsHref = this.prevLink.href;
  }

  _next() {
    if (!this.nextLink) {
      return;
    }
    this.levelsHref = this.nextLink.href;
  }

  async _onUpdateLevels(e) {
    const { entity } = e.detail;
    if (!entity) {
      this.readings = [];
      return;
    }

    this.prevLink = entity.getLinkByRel('prev');
    this.nextLink = entity.getLinkByRel('next');

    this.start = entity.properties.start;
    this.end = entity.properties.end;
    this.dispatchEvent(new CustomEvent('update', { detail: { entity } }));
    this.disableNext = DateTime.fromISO(this.end).plus({ hour: 1 }) > DateTime.now();

    // sort levels by publishedAt
    const levels = entity.getSubEntitiesByClass('level');
    levels.sort((a, b) => a.properties.publishedAt.localeCompare(b.properties.publishedAt));
    const readings = [];
    const { volumes: { total: totalVolume }, bulkDensity } = this.entity.properties;
    for (let i = 0; i < levels.length; ++i) {
      const level = levels[i];
      const reading = { level };
      const refillRange = getRefillRange({
        currentLevelIndex: i,
        levels,
        totalVolume,
        bulkDensity,
      });

      if (refillRange !== undefined) {
        const rangeHtml = html`
          ${this.binLevelMeasurement.convert({ volume: refillRange.low, bulkDensity, totalVolume })}
          - ${this.binLevelMeasurement.convert({ volume: refillRange.high, bulkDensity, totalVolume })}
          <span class='unit'>${this.binLevelMeasurement.unit}</span>
        `;

        reading.info = html`Refill detected: ${rangeHtml}`;
      }

      readings.unshift(reading);
    }

    this.readings = readings;
  }

  _formatVolume(val) {
    return val !== undefined ? Measurements.get('volume').html({ volume: val }) : 'unknown';
  }

  _formatWeight(volume) {
    return volume !== undefined ? Measurements.get('weight').html({ weight: volume * this.entity?.properties.bulkDensity }) : 'unknown';
  }

  _formatPercent(volume) {
    const totalVolume = this.getProperty('volumes.fullTotal');
    const bulkDensity = this.getProperty('bulkDensity');
    return volume === undefined || !bulkDensity || !totalVolume ? 'unknown' : this.binLevelMeasurement.html({ volume, bulkDensity, totalVolume });
  }

  _formatDate(date) {
    return DateTime.fromISO(date).toFormat('MMMM d, y h:mm a');
  }
}

window.customElements.define('bin-readings', BinReadings);
